import React from 'react';
import styled from 'styled-components';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';

import { SignerRoleInputs } from '../DocumentTemplateSignerRoleInputs';

import { P, H2, ButtonPrimary } from '@yuhu-inc/yuhui';
import { getTranslatedString } from 'helpers/i18n';
import { media } from 'helpers/MediaHelper';

import { StyledFooterText } from './form_fields/shared_styles/footers';

const StyledForm = styled(Form)`
  width: 50%;
  overflow-y: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  ${media.mobile`
  width: 90%;
  `};
`;

const StyledInner = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-top: 5rem;
  margin-bottom: 5rem;
  padding: 0px 40px;
  ${media.mobile`
  max-height: 550px;
  `};
`;

const StyledError = styled.div`
  margin: 0px 10px;
  color: red;
`;

const StyledButton = styled(ButtonPrimary)`
  margin-top: 25px;
  width: fit-content;
  align-self: center;
`;

const StyledP = styled(P)`
  ${media.mobile`
    padding: 0;
  `};
`;

export const DocumentTemplateRolesPage = ({
  setTemplate,
  forward,
  isLease,
  isConditionalAddendum,
  documentTemplate = {},
  ...rest
}) => {
  const onSubmit = (values) => {
    setTemplate((template) => {
      return {
        ...template,
        ...values,
      };
    });
    forward();
  };

  const minApplicants = 1;

  const TemplateRolesSchema = Yup.object().shape({
    max_num_applicants: Yup.number().min(
      minApplicants,
      getTranslatedString('landlordPortal.documentTemplate.minApplicantsMoreThanZero')
    ),
    max_num_guarantors: Yup.number().min(
      0,
      getTranslatedString('landlordPortal.documentTemplate.minGuarantorsMoreThanZero')
    ),
    max_num_occupants: Yup.number().min(
      0,
      getTranslatedString('landlordPortal.documentTemplate.minOccupantsMoreThanZero')
    ),
    max_rentable_items: Yup.number().min(
      0,
      getTranslatedString('landlordPortal.documentTemplate.rentableItems')
    ),
    max_incentives: Yup.number().min(
      0,
      getTranslatedString('landlordPortal.documentTemplate.incentives')
    ),
  });

  return (
    <Formik
      validationSchema={TemplateRolesSchema}
      onSubmit={onSubmit}
      initialValues={{
        max_num_applicants: documentTemplate.max_num_applicants || minApplicants,
        max_num_guarantors: documentTemplate.max_num_guarantors || 0,
        max_rentable_items: documentTemplate.maximum_rentable_items || 0,
        max_num_occupants: documentTemplate.max_num_occupants || 0,
        max_incentives: documentTemplate.maximum_incentives || 0,
      }}
      enableReinitialize // Reset the form's initial values if the isLease prop changes
    >
      {({ values, setFieldValue, errors }) => (
        <StyledForm {...rest}>
          <StyledInner>
            <H2 contentKey="landlordPortal.documentTemplate.definePlaceHolders" centered />
            <H2 contentKey="landlordPortal.documentTemplateDetailsForm.roles" />
            <StyledP contentKey="landlordPortal.documentTemplate.dontWorryAboutAddingRole" />
            <SignerRoleInputs
              numOfApplicants={values.max_num_applicants}
              numOfGuarantors={values.max_num_guarantors}
              numOfRentableItems={values.max_rentable_items}
              numberofOccupants={values.max_num_occupants}
              numOfIncentives={values.max_incentives}
              setFieldValue={setFieldValue}
              minApplicants={minApplicants}
              isLease={isLease}
              isConditionalAddendum={isConditionalAddendum}
            />
            {errors.max_num_applicants ? (
              <StyledError>{errors.max_num_applicants}</StyledError>
            ) : null}
            {errors.max_num_guarantors ? (
              <StyledError>{errors.max_num_guarantors}</StyledError>
            ) : null}
            {errors.max_rentable_items ? (
              <StyledError>{errors.max_rentable_items}</StyledError>
            ) : null}
            {errors.max_incentives ? <StyledError>{errors.max_incentives}</StyledError> : null}
            <StyledButton contentKey="landlordPortal.inspections.next" />
            <StyledFooterText>
              {getTranslatedString('landlordPortal.documentTemplate.completedPagesAutoSave')}
            </StyledFooterText>
          </StyledInner>
        </StyledForm>
      )}
    </Formik>
  );
};
